/**
 * Capability Hooks
 * Hooks for checking user capabilities
 */
import { useMemo } from 'react';
import { hasAnyEffectiveCapability, hasEffectiveCapability } from 'pi-kiosk-shared';
import { useAuth } from './useAuth.js';

/**
 * Hook to get all user capabilities
 */
export function useCapabilities(): readonly string[] {
  const { capabilities } = useAuth();
  return capabilities ?? [];
}

/**
 * Hook to check if user has a specific capability (direct grant only).
 */
export function useHasCapability(capability: string): boolean {
  const capabilities = useCapabilities();
  return useMemo(() => {
    if (!capabilities || !Array.isArray(capabilities)) {
      return false;
    }
    return capabilities.includes(capability);
  }, [capabilities, capability]);
}

/**
 * Hook to check effective capability (server-expanded JWT + client bridge mirror).
 */
export function useHasEffectiveCapability(required: string): boolean {
  const capabilities = useCapabilities();
  return useMemo(() => {
    if (!capabilities || !Array.isArray(capabilities)) {
      return false;
    }
    return hasEffectiveCapability(capabilities, required);
  }, [capabilities, required]);
}

/**
 * Hook to check if user has any of the specified capabilities (effective set).
 */
export function useHasAnyEffectiveCapability(requiredCapabilities: readonly string[]): boolean {
  const capabilities = useCapabilities();
  return useMemo(() => {
    if (!capabilities || !Array.isArray(capabilities)) {
      return false;
    }
    return hasAnyEffectiveCapability(capabilities, requiredCapabilities);
  }, [capabilities, requiredCapabilities]);
}

/**
 * Hook to check if user has any of the specified capabilities
 */
export function useHasAnyCapability(requiredCapabilities: readonly string[]): boolean {
  const capabilities = useCapabilities();
  return useMemo(() => {
    if (!capabilities || !Array.isArray(capabilities)) {
      return false;
    }
    return requiredCapabilities.some((cap) => capabilities.includes(cap));
  }, [capabilities, requiredCapabilities]);
}

/**
 * Hook to check if user can access an area
 * Checks if user has any capability that starts with the area prefix
 */
export function useCanAccess(area: string): boolean {
  const capabilities = useCapabilities();
  return useMemo(() => {
    if (!capabilities || !Array.isArray(capabilities)) {
      return false;
    }
    const areaPrefix = area.endsWith(':') ? area : `${area}:`;
    return capabilities.some((cap) => cap.startsWith(areaPrefix));
  }, [capabilities, area]);
}

/**
 * Hook to check if user has all of the specified capabilities
 */
export function useHasAllCapabilities(requiredCapabilities: readonly string[]): boolean {
  const capabilities = useCapabilities();
  return useMemo(() => {
    if (!capabilities || !Array.isArray(capabilities)) {
      return false;
    }
    return requiredCapabilities.every((cap) => capabilities.includes(cap));
  }, [capabilities, requiredCapabilities]);
}

/**
 * Hook to check if user has all of the specified capabilities (effective set).
 */
export function useHasAllEffectiveCapabilities(requiredCapabilities: readonly string[]): boolean {
  const capabilities = useCapabilities();
  return useMemo(() => {
    if (!capabilities || !Array.isArray(capabilities)) {
      return false;
    }
    return requiredCapabilities.every((cap) => hasEffectiveCapability(capabilities, cap));
  }, [capabilities, requiredCapabilities]);
}
